/**
 * use-payment-method-policies — paymentMethodPoliciesApi（ADM-PAY-S008 通道启停 + 排序 + Region 覆盖）
 *
 * 契约源 dev-docs `02-API契约/A8-运营支付.md` §7.8.1~§7.8.4。每方法一次 apiRequest（`~/api/client`
 * 内部走 authedFetch：注入 JWT + 401 续期重放 + 解 envelope，禁裸 fetch 手注 Bearer）；请求 / 响应
 * 类型全取 `@novel-admin/shared`，web 侧不重定义 wire 形状。
 *
 * **三态在本层不合并**（§7.8.0，两者端点不同、serving 后果相反）：
 *   - `replace({ payment_methods: [] })` = 空策略：组保留、成员清空 → 该 Region / 全局返空态，**不回退全局**
 *   - `remove({ app_id, region_code })`  = 删组：该 Region 恢复继承全局策略
 * 故不提供「清空即删组」之类的便捷封装——调用方必须显式选其一。全局组（`region_code: null`）
 * 只能 replace：DELETE 的 query schema 要求 `region_code` 非空，全局组不可删。
 */

import type {
  DeletePaymentMethodPolicyResponse,
  PaymentMethodPolicyResponse,
  ReorderPaymentMethodPolicyRequest,
  UpsertPaymentMethodPolicyRequest,
} from '@novel-admin/shared';
import { apiRequest } from '~/api/client';

export const paymentMethodPoliciesApi = {
  /** 列某 App 全部策略组（§7.8.1，不分页；全局组恒首位，零成员组照常返回 payment_methods: []）*/
  list: (appId: string) =>
    apiRequest<PaymentMethodPolicyResponse[]>({
      method: 'GET',
      path: '/api/v1/payment-method-policies',
      query: { app_id: appId },
    }),
  /** 建组 + 整组替换成员（§7.8.2，operator+ mfa）；数组位置即 sort_order，不传 sort_order 入参 */
  replace: (body: UpsertPaymentMethodPolicyRequest) =>
    apiRequest<PaymentMethodPolicyResponse>({
      method: 'PUT',
      path: '/api/v1/payment-method-policies',
      body,
    }),
  /**
   * 条件调序（§7.8.10，operator+ mfa）：只重排既有成员；携带预期成员集合，服务端锁内核验一致才写。
   * 成员已变化 → `RESOURCE_CONFLICT`（`details.reason='POLICY_MEMBERS_CHANGED'`），组不存在 → `RESOURCE_NOT_FOUND`，不 upsert。
   */
  reorder: (policyId: string, body: ReorderPaymentMethodPolicyRequest) =>
    apiRequest<PaymentMethodPolicyResponse>({
      method: 'PUT',
      path: `/api/v1/payment-method-policies/${policyId}/order`,
      body,
    }),
  /** 删 Region 覆盖组（§7.8.4，operator+ mfa）→ 该 Region 恢复继承全局；region_code 必须非空 */
  remove: (params: { app_id: string; region_code: string }) =>
    apiRequest<DeletePaymentMethodPolicyResponse>({
      method: 'DELETE',
      path: '/api/v1/payment-method-policies',
      query: { app_id: params.app_id, region_code: params.region_code },
    }),
};
